import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { wsRequest } from './websocketRequest'
import { applyDemoModeByUserInfo } from './demoMode'

// 全局token存储
let globalToken: string = ''
let globalUserInfo: any = {}
let globalIsAdmin = false

// 设置全局token（由main.ts中的qiankun钩子调用）
export const setGlobalToken = (token: string) => {
  globalToken = token
}

// 设置全局用户信息（始终覆盖为传入值，不做本地持久化缓存）
export const setGlobalUserInfo = (userInfo: any) => {
  globalUserInfo = userInfo && typeof userInfo === 'object' ? { ...userInfo } : {}
  // 尽早根据 username 切换演示模式，避免子组件先发起真实请求
  applyDemoModeByUserInfo(globalUserInfo)
}

// 清空全局用户信息（卸载时调用，避免下次挂载读到旧数据）
export const clearGlobalUserInfo = () => {
  globalUserInfo = {}
  applyDemoModeByUserInfo({})
}

// 设置全局 isAdmin 状态，供业务按需读取
export const setGlobalIsAdmin = (isAdmin: boolean) => {
  globalIsAdmin = !!isAdmin
}

// 获取当前token
export const getCurrentToken = () => globalToken

// 获取当前用户信息（返回拷贝，避免外部误改内存态）
export const getCurrentUserInfo = () => ({ ...globalUserInfo })

// 获取当前 isAdmin 状态
export const getCurrentIsAdmin = () => globalIsAdmin

// 创建axios实例
const service: AxiosInstance = axios.create({
  // baseURL: 'http://127.0.0.1:8000',
  // baseURL: 'http://192.168.1.30:8000',
  baseURL: 'https://api.ldsz.cc', // API基础路径
  timeout: 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
})

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 自动添加token到请求头
    if (globalToken) {
      config.headers.set('Authorization', `JWT ${globalToken}`)
    }

    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      }
    }

    return config
  },
  (error) => {
    console.error('[API] 请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, status } = response

    // 请求成功
    if (status === 200) {
      // 根据后端返回的数据结构调整
      if (data.code === 2000) {
        return data.data || data
      } else {
        // 业务错误
        const errorMsg = data.message || data.msg || '请求失败'
        console.error('[API] 业务错误:', errorMsg)
        throw new Error(errorMsg)
      }
    }

    return data
  },
  (error) => {
    console.error('[API] 响应错误:', error)

    // 处理HTTP状态码错误
    if (error.response) {
      const { status, data } = error.response
      let errorMessage = '请求失败'

      switch (status) {
        case 401:
          errorMessage = 'Token已过期，请重新登录'
          break
        case 403:
          errorMessage = '没有权限访问'
          break
        case 404:
          errorMessage = '请求的资源不存在'
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        default:
          errorMessage = data?.message || data?.msg || `请求失败 (${status})`
      }

      throw new Error(errorMessage)
    } else if (error.request) {
      // 网络错误
      throw new Error('网络连接失败，请检查网络')
    } else {
      throw new Error(error.message || '请求配置错误')
    }
  }
)

// 是否使用 WebSocket 进行请求（可通过环境变量或配置控制）
// 默认不使用 WebSocket，除非显式指定
const USE_WEBSOCKET = process.env.VUE_APP_USE_WEBSOCKET === 'true'

// 通用请求方法（默认使用 HTTP）
export const request = {
  get: <T = any>(url: string, params?: any, useWebSocket: boolean = false): Promise<T> => {
    if (USE_WEBSOCKET && useWebSocket) {
      return wsRequest.get<T>(url, params).catch((error) => {
        console.warn('[WebSocket] 请求失败，回退到 HTTP:', error)
        return service.get(url, { params })
      })
    }
    return service.get(url, { params })
  },

  post: <T = any>(url: string, data?: any, useWebSocket: boolean = false): Promise<T> => {
    if (USE_WEBSOCKET && useWebSocket) {
      return wsRequest.post<T>(url, data).catch((error) => {
        console.warn('[WebSocket] 请求失败，回退到 HTTP:', error)
        return service.post(url, data)
      })
    }
    return service.post(url, data)
  },

  put: <T = any>(url: string, data?: any, useWebSocket: boolean = false): Promise<T> => {
    if (USE_WEBSOCKET && useWebSocket) {
      return wsRequest.put<T>(url, data).catch((error) => {
        console.warn('[WebSocket] 请求失败，回退到 HTTP:', error)
        return service.put(url, data)
      })
    }
    return service.put(url, data)
  },

  delete: <T = any>(url: string, params?: any, useWebSocket: boolean = false): Promise<T> => {
    if (USE_WEBSOCKET && useWebSocket) {
      return wsRequest.delete<T>(url, params).catch((error) => {
        console.warn('[WebSocket] 请求失败，回退到 HTTP:', error)
        return service.delete(url, { params })
      })
    }
    return service.delete(url, { params })
  },
}

// 专门使用 WebSocket 请求的方法
export const wsRequestOnly = {
  get: <T = any>(url: string, params?: any): Promise<T> => {
    return wsRequest.get<T>(url, params)
  },

  post: <T = any>(url: string, data?: any): Promise<T> => {
    return wsRequest.post<T>(url, data)
  },

  put: <T = any>(url: string, data?: any): Promise<T> => {
    return wsRequest.put<T>(url, data)
  },

  delete: <T = any>(url: string, params?: any): Promise<T> => {
    return wsRequest.delete<T>(url, params)
  },
}

// 直接使用 WebSocket 请求（不依赖配置）
export const wsRequestDirect = wsRequest

export default service
